import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { TESTIDS } from "@/constants/testIds";
import useSeo from "@/hooks/useSeo";

const Impressum = () => {
  useSeo({
    title: "Impressum | Prozenthaus Schenklengsfeld",
    description:
      "Impressum & rechtliche Angaben gemäß § 5 TMG für Prozenthaus, Inhaber Ron Kowalewsky, Landeckerstraße 20A, 36277 Schenklengsfeld.",
    canonical: "https://prozenthaus.shop/impressum",
    robots: "index, follow",
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <section
      data-testid={TESTIDS.impressumPage}
      className="border-b border-[var(--pz-border)]"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <Link
          to="/"
          data-testid={TESTIDS.legalBackHome}
          className="inline-flex items-center gap-2 pz-link text-sm mb-10"
        >
          <ArrowLeft size={16} /> Zurück zur Startseite
        </Link>

        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="pz-eyebrow-gold mb-4">Rechtliches</div>
            <h1 className="font-display text-5xl sm:text-6xl font-black leading-[1.02] tracking-tight">
              Impressum.
            </h1>
            <p className="mt-6 text-[var(--pz-text-muted)] text-base leading-relaxed">
              Angaben gemäß § 5 TMG sowie § 18 Abs. 2 Medienstaatsvertrag (MStV).
            </p>
          </div>

          <div className="lg:col-span-8 pz-legal-content">
            <h2>Anbieter</h2>
            <p>
              <strong>Prozenthaus</strong>
              <br />
              Inhaber: Ron Kowalewsky
              <br />
              Landeckerstraße 20A
              <br />
              36277 Schenklengsfeld
              <br />
              Deutschland
            </p>

            <h2>Kontakt</h2>
            <p>
              Telefon: <a href="tel:+4966292160485">06629 2160485</a>
              <br />
              E-Mail:{" "}
              <a href="mailto:team@prozenthaus.shop">team@prozenthaus.shop</a>
            </p>

            <h2>Umsatzsteuer-Identifikationsnummer</h2>
            <p>
              Umsatzsteuer-Identifikationsnummer gemäß § 27 a Umsatzsteuergesetz:
              <br />
              <strong>DE403657090</strong>
            </p>

            <h2>Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV</h2>
            <p>
              Ron Kowalewsky
              <br />
              Landeckerstraße 20A
              <br />
              36277 Schenklengsfeld
            </p>

            <h2>EU-Streitschlichtung</h2>
            <p>
              Die Europäische Kommission stellt eine Plattform zur
              Online-Streitbeilegung (OS) bereit:{" "}
              <a
                href="https://ec.europa.eu/consumers/odr/"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://ec.europa.eu/consumers/odr/
              </a>
              <br />
              Unsere E-Mail-Adresse finden Sie oben im Impressum.
            </p>

            <h2>Verbraucherstreitbeilegung / Universalschlichtungsstelle</h2>
            <p>
              Wir sind nicht bereit oder verpflichtet, an Streitbeilegungs­verfahren
              vor einer Verbraucherschlichtungsstelle teilzunehmen.
            </p>

            <h2>Haftung für Inhalte</h2>
            <p>
              Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte
              auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach
              §§ 8 bis 10 TMG sind wir als Diensteanbieter jedoch nicht
              verpflichtet, übermittelte oder gespeicherte fremde Informationen zu
              überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige
              Tätigkeit hinweisen.
            </p>
            <p>
              Verpflichtungen zur Entfernung oder Sperrung der Nutzung von
              Informationen nach den allgemeinen Gesetzen bleiben hiervon
              unberührt. Eine diesbezügliche Haftung ist jedoch erst ab dem
              Zeitpunkt der Kenntnis einer konkreten Rechtsverletzung möglich. Bei
              Bekanntwerden von entsprechenden Rechtsverletzungen werden wir diese
              Inhalte umgehend entfernen.
            </p>

            <h2>Haftung für Links</h2>
            <p>
              Unser Angebot enthält Links zu externen Websites Dritter, auf deren
              Inhalte wir keinen Einfluss haben. Deshalb können wir für diese
              fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der
              verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der
              Seiten verantwortlich. Die verlinkten Seiten wurden zum Zeitpunkt der
              Verlinkung auf mögliche Rechtsverstöße überprüft. Rechtswidrige
              Inhalte waren zum Zeitpunkt der Verlinkung nicht erkennbar.
            </p>
            <p>
              Eine permanente inhaltliche Kontrolle der verlinkten Seiten ist
              jedoch ohne konkrete Anhaltspunkte einer Rechtsverletzung nicht
              zumutbar. Bei Bekanntwerden von Rechtsverletzungen werden wir
              derartige Links umgehend entfernen.
            </p>

            <h2>Urheberrecht</h2>
            <p>
              Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen
              Seiten unterliegen dem deutschen Urheberrecht. Die Vervielfältigung,
              Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der
              Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung des
              jeweiligen Autors bzw. Erstellers.
            </p>
            <p>
              Soweit die Inhalte auf dieser Seite nicht vom Betreiber erstellt
              wurden, werden die Urheberrechte Dritter beachtet. Insbesondere
              werden Inhalte Dritter als solche gekennzeichnet. Sollten Sie
              trotzdem auf eine Urheberrechtsverletzung aufmerksam werden, bitten
              wir um einen entsprechenden Hinweis. Bei Bekanntwerden von
              Rechtsverletzungen werden wir derartige Inhalte umgehend entfernen.
            </p>

            <h2>Bildnachweise</h2>
            <p>
              Verwendete Bilder stammen aus lizenzfreien Quellen (u. a. Pexels,
              Unsplash) sowie eigenen Aufnahmen.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Impressum;
